import { FC, memo, useCallback } from 'react'
import { Modal } from 'components/layouts/modal/modal'
import { useTranslation } from '@crew/modules/i18n'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import classNames from 'classnames'
import { useSystemPermissions } from '@crew/hooks'
import MaterialSymbolsCircle from '~icons/material-symbols/circle'
import { CrewScrollView } from 'components/devextreme/crewScrollView'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { ContractPlan } from '@crew/enums/dist/app'

export type CrewNavigateToPaidPlanDialogProps = {
  isOpen: boolean
  onClose: () => void
}

// プランの機能情報
type PlanFeature = {
  text: string
  highlight?: boolean
}

// プラン情報
type Plan = {
  name: string
  value: string
  price: {
    monthly: number
    yearly: number
    yearlyDiscount?: number
  }
  features: PlanFeature[]
  isRecommended?: boolean
  isCurrentPlan?: boolean
}

export const CrewNavigateToPaidPlanDialog: FC<CrewNavigateToPaidPlanDialogProps> =
  memo((props) => {
    const { t } = useTranslation()
    const { navigate } = useCrewNavigate()

    // 契約情報へのアクセス権限があるか
    const { hasSysContractViewEditPermission } = useSystemPermissions()

    // プランデータの定義
    // TODO: 将来的にはAPIから取得するように変更予定
    //       https://break-tmc.atlassian.net/browse/CREW-20548
    const plans: Plan[] = [
      // フリー
      {
        name: t('label.planType.free'),
        value: ContractPlan.Free,
        price: {
          monthly: 0,
          yearly: 0,
        },
        features: [
          // 会議
          {
            text: t('label.planFeatures.meeting.free'),
          },
          // ストレージ
          {
            text: t('label.planFeatures.storage', {
              maxStorage: 5,
            }),
          },
          // AI生成
          { text: t('label.planFeatures.aiCredits', { maxCredits: 10 }) },
          // ユーザー数
          {
            text: t('label.planFeatures.userLimit.free'),
          },
          // タスク階層の設定
          {
            text: t('label.planFeatures.taskHierarchyLevels.free'),
          },
        ],
        isCurrentPlan: true, // このダイアログはフリープランの場合のみ表示されるため、現在のプラン確認処理はここでは行わない
      },
      // スタンダード
      {
        name: t('label.planType.standard'),
        value: ContractPlan.Standard,
        price: {
          monthly: 400,
          yearly: 500,
          yearlyDiscount: 1200,
        },
        features: [
          // 会議
          {
            text: t('label.planFeatures.meeting.standard'),
          },
          // ストレージ
          {
            text: t('label.planFeatures.storage', {
              maxStorage: 100,
            }),
          },
          // AI生成
          { text: t('label.planFeatures.aiCredits', { maxCredits: 30 }) },
          // ユーザー数
          {
            text: t('label.planFeatures.userLimit.standard'),
          },
          // タスクのCSV入出力
          {
            text: t('label.planFeatures.taskCsvImportExport'),
            highlight: true,
          },
          // タスクの依存関係の設定
          // TODO: CREW-20546で対応
          // {
          //   text: t('label.planFeatures.taskDependency'),
          //   highlight: true,
          // },
          // タスク階層の設定
          {
            text: t('label.planFeatures.taskHierarchyLevels.standard'),
            highlight: true,
          },
        ],
        // おすすめプラン
        isRecommended: true,
      },
      // プロフェッショナル
      {
        name: t('label.planType.professional'),
        value: ContractPlan.Professional,
        price: {
          monthly: 900,
          yearly: 1050,
          yearlyDiscount: 1800,
        },
        features: [
          // 会議
          {
            text: t('label.planFeatures.meeting.professional'),
          },
          // ストレージ
          {
            text: t('label.planFeatures.storage', {
              maxStorage: 100,
            }),
          },
          // AI生成
          { text: t('label.planFeatures.aiCredits', { maxCredits: 100 }) },
          // ユーザー数
          {
            text: t('label.planFeatures.userLimit.professional'),
          },
          // タスクのCSV入出力
          {
            text: t('label.planFeatures.taskCsvImportExport'),
            highlight: true,
          },
          // タスクの依存関係の設定
          // TODO: CREW-20546で対応
          // {
          //   text: t('label.planFeatures.taskDependency'),
          //   highlight: true,
          // },
          // タスク階層の設定
          {
            text: t('label.planFeatures.taskHierarchyLevels.professional'),
            highlight: true,
          },
          // セキュリティ
          { text: t('label.planFeatures.security') },
          // サポート
          { text: t('label.planFeatures.support') },
        ],
      },
    ]

    // アップグレードボタンのクリック時
    const handleUpgradeClick = useCallback(
      (selectedPlan: Plan) => {
        // ダイアログを閉じる
        props.onClose()

        // 支払い方法登録画面へ遷移
        navigate('/contract/register-payment-method', {
          plan: selectedPlan.value,
        })
      },
      [props, navigate]
    )

    return (
      <Modal
        headerLess
        isOpen={props.isOpen}
        onClose={props.onClose}
        className="!bg-crew-blue-50 dark:!bg-crew-gray-900 px-10 py-8"
      >
        <CrewScrollView>
          <div className="space-y-5">
            {/* ヘッダー */}
            <div className="flex items-center justify-center gap-1">
              <img
                src="/images/logo192.png"
                alt="CrewWorks Logo"
                className="w-9 h-9 object-contain"
              />
              <div className="text-4xl font-bold crew-text-gray-1">
                {t('label.featuresOfThePlan')}
              </div>
            </div>

            {/* 契約情報へのアクセス権限がない場合、管理者のみアップグレード可能な旨を表示 */}
            {!hasSysContractViewEditPermission && (
              <div className="mb-5 text-center">
                {t('label.upgradeAdminOnlyMessage.prefix')}
                <span className="text-crew-blue-500 dark:text-crew-blue-400">
                  {t('label.roles.sysAdmin')}
                </span>
                {t('label.upgradeAdminOnlyMessage.suffix')}
              </div>
            )}

            {/* プラン比較テーブル */}
            <div className="pt-5">
              {/* プランヘッダー */}
              <div className="grid grid-cols-3 bg-crew-blue-50 dark:bg-crew-gray-900 min-w-fit">
                {plans.map((plan) => (
                  <div
                    key={plan.name}
                    className={classNames(
                      'relative p-3 text-center border-2 first:rounded-tl-lg last:rounded-tr-lg',
                      // [枠線の色設定] おすすめプランの場合：強調色
                      plan.isRecommended &&
                        'border-crew-cyan-500 dark:border-crew-cyan-700 border-b-crew-gray-300 dark:border-b-crew-gray-700',
                      // [枠線の色設定] その他のプランの場合：通常色
                      !plan.isRecommended &&
                        'border-crew-gray-300 dark:border-crew-gray-700'
                    )}
                  >
                    {/* おすすめバッジ */}
                    {plan.isRecommended && (
                      <div className="absolute -top-6 -left-0.5 -right-0.5 z-10">
                        <div className="bg-crew-cyan-500 dark:bg-crew-cyan-700 text-crew-white py-1 text-sm text-center">
                          {t('label.recommended')}
                        </div>
                      </div>
                    )}

                    {/* プラン名 */}
                    <div className="text-lg font-bold">{plan.name}</div>
                  </div>
                ))}
              </div>

              {/* 機能リスト */}
              <div className="grid grid-cols-3 min-w-fit">
                {plans.map((plan) => (
                  <div
                    key={plan.name}
                    className={classNames(
                      'p-4 text-center border-l-2 border-r-2 border-b-2 first:rounded-bl-lg last:rounded-br-lg space-y-4 bg-crew-white dark:bg-crew-gray-800',
                      // おすすめプランの場合、枠線を強調色
                      plan.isRecommended &&
                        'border-crew-cyan-500 dark:border-crew-cyan-700',
                      // その他のプランの場合、枠線を通常色
                      !plan.isRecommended &&
                        'border-crew-gray-300 dark:border-crew-gray-700'
                    )}
                  >
                    {/* 価格情報 */}
                    <div className="space-y-0.5">
                      <div>{t('label.pricePerUserPerMonth')}</div>
                      <div className="space-x-0.5">
                        <span className="text-xl">¥</span>
                        <span className="text-5xl">
                          {plan.price.monthly.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* 年間契約でxx円お得 */}
                    <div className="h-9">
                      {plan.price.yearlyDiscount && (
                        <div className="bg-crew-yellow-50 dark:bg-crew-yellow-950 rounded py-1">
                          <div className="text-xs">
                            {t('label.monthlyContractPrice', {
                              price: plan.price.yearly.toLocaleString(),
                            })}
                          </div>
                          <div>
                            → {t('label.yearlyContractBenefit.yearly')}
                            <span className="text-crew-orange-600 dark:text-crew-orange-400 font-bold">
                              {t('label.yearlyContractBenefit.maxSavings', {
                                amount:
                                  plan.price.yearlyDiscount.toLocaleString(),
                              })}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* アクションボタン */}
                    {plan.isCurrentPlan ? (
                      // 現在のプラン
                      <CrewButton
                        text={t('label.currentPlan')}
                        className={'w-full'}
                        disabled={true}
                      />
                    ) : (
                      // アップグレード可能なプラン
                      <CrewButton
                        text={t('label.upgradeNow')}
                        type="primary"
                        className={'w-full'}
                        stylingMode={
                          // おすすめ または ボタンを無効化する場合はcontained、それ以外はoutlined
                          plan.isRecommended ||
                          !hasSysContractViewEditPermission
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() => handleUpgradeClick(plan)}
                        disabled={!hasSysContractViewEditPermission}
                      />
                    )}

                    {/* 機能リスト */}
                    <div className="space-y-1">
                      {plan.features.map((feature, index) => (
                        <div
                          key={index}
                          className={classNames(
                            'flex items-center gap-1',
                            feature.highlight &&
                              'text-crew-cyan-600 dark:text-crew-cyan-500'
                          )}
                        >
                          <MaterialSymbolsCircle
                            width={9}
                            height={9}
                            className="flex-shrink-0"
                          />
                          <div className="text-left w-full whitespace-nowrap">
                            {feature.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CrewScrollView>
      </Modal>
    )
  })
